import { useState } from "react";
import axios from "axios";
import { API, USDC_BY_CHAIN, API_SWAP_CHAIN_IDS } from "../utils/constants";
import { switchToChain, getChainConfig } from "../utils/addNetwork";
import { executeV4Swap, quoteV4Swap } from "../utils/v4Swap";

const NATIVE_ETH = "0x0000000000000000000000000000000000000000";

export const useSwap = (walletClient, switchChain, chain) => {
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [txHash, setTxHash] = useState("");
  const [quoteInfo, setQuoteInfo] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  /**
   * Resolve the EIP-1193 provider from the connected wagmi walletClient.
   * Falls back to window.ethereum only when wagmi hasn't hydrated yet.
   * This ensures transactions always go through the wallet the user connected
   * with (e.g. MetaMask) rather than any other injected wallet.
   */
  const resolveProvider = () => {
    if (walletClient?.transport) return walletClient.transport;
    if (typeof window !== "undefined" && window.ethereum) return window.ethereum;
    throw new Error("No wallet provider found. Please connect your wallet.");
  };

  /**
   * Fetch a price estimate without sending any transactions.
   * Sets quoteInfo on success or error on failure.
   */
  const getQuote = async ({ amount, swapChainId, direction, apiKey }) => {
    try {
      setQuoteLoading(true);
      setQuoteInfo(null);
      setError("");

      const provider = resolveProvider();
      const accounts = await provider.request({ method: "eth_requestAccounts" });
      const walletAddress = accounts[0];

      const usdcAddress = USDC_BY_CHAIN[swapChainId];
      if (!usdcAddress) throw new Error("Swap not supported on this chain");

      const tokenIn = direction === "ethToUsdc" ? NATIVE_ETH : usdcAddress;
      const tokenOut = direction === "ethToUsdc" ? usdcAddress : NATIVE_ETH;
      const inDecimals = direction === "ethToUsdc" ? 18 : 6;
      const outDecimals = direction === "ethToUsdc" ? 6 : 18;
      const tokenOutSymbol = direction === "ethToUsdc" ? "USDC" : "ETH";
      const amountMinor = BigInt(
        Math.round(parseFloat(amount) * 10 ** inDecimals)
      ).toString();

      if (!API_SWAP_CHAIN_IDS.has(swapChainId)) {
        // V4 on-chain quoter (read-only eth_call)
        const { amountOut, fee } = await quoteV4Swap({
          amountMinor,
          direction,
          swapChainId,
          provider,
        });
        const amountOutFormatted = (
          Number(amountOut) / 10 ** outDecimals
        ).toFixed(outDecimals === 6 ? 2 : 6);
        setQuoteInfo({
          amountOutFormatted,
          tokenOut: tokenOutSymbol,
          routing: `V4 Universal Router (fee ${fee})`,
        });
      } else {
        // Trading API quote
        if (!apiKey) throw new Error("Uniswap API key is required for this chain");
        const headers = { "x-api-key": apiKey };
        const quoteResponse = await axios.post(
          `${API.UNISWAP_API_URL}/quote`,
          {
            swapper: walletAddress,
            tokenInChainId: swapChainId,
            tokenOutChainId: swapChainId,
            tokenIn,
            tokenOut,
            amount: amountMinor,
            type: "EXACT_INPUT",
          },
          { headers },
        );
        const { quote, routing } = quoteResponse.data;
        const outRaw =
          quote?.output?.amount ??
          quote?.orderInfo?.outputs?.[0]?.startAmount ??
          "0";
        const amountOutFormatted = (
          Number(outRaw) / 10 ** outDecimals
        ).toFixed(outDecimals === 6 ? 2 : 6);
        setQuoteInfo({
          amountOutFormatted,
          tokenOut: tokenOutSymbol,
          routing: routing || "Trading API",
        });
      }
    } catch (err) {
      console.error("Quote failed:", err);
      setError(
        err.response?.data?.detail ||
        err.response?.data?.errorCode ||
        err.message ||
        "Quote failed",
      );
      setQuoteInfo(null);
    } finally {
      setQuoteLoading(false);
    }
  };

  const executeSwap = async ({
    amount,
    swapChainId,
    direction, // "ethToUsdc" or "usdcToEth"
    apiKey,
  }) => {
    try {
      setLoading(true);
      setError("");
      setTxHash("");
      setStatus("Initializing swap...");

      if (!apiKey && API_SWAP_CHAIN_IDS.has(swapChainId)) {
        throw new Error("Uniswap API key is required for this chain");
      }

      const provider = resolveProvider();

      const headers = apiKey ? { "x-api-key": apiKey } : {};

      // Ensure we're on the correct chain
      if (chain?.id !== swapChainId) {
        setStatus("Switching to swap chain...");
        const chainConfig = getChainConfig(swapChainId);
        await switchToChain(switchChain, swapChainId, chainConfig);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Get signer address from the connected wallet
      const accounts = await provider.request({
        method: "eth_requestAccounts",
      });
      const walletAddress = accounts[0];

      const usdcAddress = USDC_BY_CHAIN[swapChainId];
      if (!usdcAddress) throw new Error("Swap not supported on this chain");

      // Determine token in/out based on direction
      const tokenIn = direction === "ethToUsdc" ? NATIVE_ETH : usdcAddress;
      const tokenOut = direction === "ethToUsdc" ? usdcAddress : NATIVE_ETH;

      // Convert amount to minor units (ETH: 18 decimals, USDC: 6 decimals)
      const decimals = direction === "ethToUsdc" ? 18 : 6;
      const amountMinor = BigInt(
        Math.round(parseFloat(amount) * 10 ** decimals)
      ).toString();

      // ── Route: V4 Universal Router for chains not supported by Trading API ────
      if (!API_SWAP_CHAIN_IDS.has(swapChainId)) {
        setStatus("Using Uniswap V4 Universal Router...");
        const hash = await executeV4Swap({
          walletAddress,
          direction,
          amountMinor,
          swapChainId,
          setStatus,
          provider,
          waitForTx: (h) => waitForTx(h, provider),
        });
        setTxHash(hash);
        setStatus("Waiting for V4 swap confirmation...");
        await waitForTx(hash, provider);
        setStatus("✅ Swap completed successfully!");
        setLoading(false);
        return;
      }

      // ── Trading API path (ETH Sepolia + Unichain Sepolia) ──────────────────
      setStatus("Checking token approval...");
      const approvalResponse = await axios.post(
        `${API.UNISWAP_API_URL}/check_approval`,
        {
          walletAddress,
          amount: amountMinor,
          token: tokenIn,
          chainId: swapChainId,
          tokenOut: tokenOut,
          tokenOutChainId: swapChainId,
        },
        { headers },
      );

      // If the token requires an approval reset before setting a new amount,
      // the API returns a cancel transaction we must send first.
      if (approvalResponse.data.cancel) {
        setStatus("Resetting previous approval...");
        const cancelTx = approvalResponse.data.cancel;
        const cancelHash = await provider.request({
          method: "eth_sendTransaction",
          params: [{
            from: cancelTx.from || walletAddress,
            to: cancelTx.to,
            data: cancelTx.data,
            value: cancelTx.value || "0x0",
            ...(cancelTx.gasLimit ? { gas: cancelTx.gasLimit } : {}),
          }],
        });
        setStatus("Waiting for approval reset...");
        await waitForTx(cancelHash, provider);
        setStatus("✓ Previous approval reset!");
      }

      // Send the actual approval transaction (approve Permit2 to spend the token)
      if (approvalResponse.data.approval) {
        setStatus("Approving token spend...");
        const approveTx = approvalResponse.data.approval;
        const approveTxHash = await provider.request({
          method: "eth_sendTransaction",
          params: [{
            from: approveTx.from || walletAddress,
            to: approveTx.to,
            data: approveTx.data,
            value: approveTx.value || "0x0",
            ...(approveTx.gasLimit ? { gas: approveTx.gasLimit } : {}),
          }],
        });
        setStatus("Waiting for approval confirmation...");
        await waitForTx(approveTxHash, provider);
        setStatus("✓ Approval confirmed!");
      }

      // STEP 2: Get quote
      setStatus("Fetching swap quote...");
      const quoteResponse = await axios.post(
        `${API.UNISWAP_API_URL}/quote`,
        {
          swapper: walletAddress,
          tokenInChainId: swapChainId,
          tokenOutChainId: swapChainId,
          tokenIn: tokenIn,
          tokenOut: tokenOut,
          amount: amountMinor,
          type: "EXACT_INPUT",
        },
        { headers },
      );
      const { quote, permitData, routing } = quoteResponse.data;

      // STEP 3: Sign permit if needed
      let signature;
      if (permitData) {
        setStatus("Signing permit...");
        // Use eth_signTypedData_v4 for EIP-712
        const typedData = {
          types: permitData.types,
          domain: permitData.domain,
          primaryType: Object.keys(permitData.types).find(
            (k) => k !== "EIP712Domain",
          ),
          message: permitData.values,
        };
        signature = await provider.request({
          method: "eth_signTypedData_v4",
          params: [walletAddress, JSON.stringify(typedData)],
        });
      }

      // STEP 4: Execute swap or order
      setStatus("Executing swap...");

      if (
        routing === "CLASSIC" ||
        routing === "WRAP" ||
        routing === "UNWRAP" ||
        routing === "BRIDGE"
      ) {
        const swapBody = { quote };
        if (signature) swapBody.signature = signature;
        if (permitData) swapBody.permitData = permitData;

        const swapResponse = await axios.post(
          `${API.UNISWAP_API_URL}/swap`,
          swapBody,
          { headers },
        );

        const hash = await provider.request({
          method: "eth_sendTransaction",
          params: [swapResponse.data.swap],
        });
        setTxHash(hash);
        setStatus("Waiting for swap confirmation...");
        await waitForTx(hash, provider);
        setStatus("✅ Swap completed successfully!");
      } else if (
        routing === "DUTCH_V2" ||
        routing === "DUTCH_V3" ||
        routing === "PRIORITY"
      ) {
        const orderResponse = await axios.post(
          `${API.UNISWAP_API_URL}/order`,
          {
            signature: signature,
            quote: quote,
          },
          { headers },
        );
        setStatus("✅ Order submitted successfully!");
        if (orderResponse.data?.orderId) {
          setTxHash(orderResponse.data.orderId);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error("Swap failed:", err);
      const message =
        err.response?.data?.detail ||
        err.response?.data?.errorCode ||
        err.message ||
        "Swap failed";
      setError(message);
      setStatus("");
      setLoading(false);
    }
  };

  return {
    status,
    loading,
    error,
    txHash,
    executeSwap,
    getQuote,
    quoteInfo,
    quoteLoading,
  };
};

// Simple tx receipt poller — uses the connected wallet's provider, not window.ethereum
async function waitForTx(hash, provider) {
  for (let i = 0; i < 60; i++) {
    const receipt = await provider.request({
      method: "eth_getTransactionReceipt",
      params: [hash],
    });
    if (receipt) return receipt;
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new Error("Transaction confirmation timeout");
}
